/**
 * Targeted Products Swiper Functionality
 * Handles SwiperJS initialization for targeted products sections
 */

// Type declarations for global objects
/* global Swiper */

class SuniceTargetedProductsSwiper {
  constructor() {
    this.swiperInstances = new Map();
  }

  initializeTargetedProductsSwiper(swiperContainer) { // @ts-ignore
    if (!swiperContainer) return;
    
    const containerId = swiperContainer.id || `targeted-products-swiper-${Date.now()}`;
    if (!swiperContainer.id) {
      swiperContainer.id = containerId;
    }
    
    // Check if this swiper is already initialized - destroy first if so (for theme editor)
    if (this.swiperInstances.has(containerId)) {
      const existingSwiper = this.swiperInstances.get(containerId);
      if (existingSwiper && existingSwiper.destroy) {
        existingSwiper.destroy(true, true);
      }
      this.swiperInstances.delete(containerId);
    }
    
    // Reset initialization flags
    swiperContainer.classList.remove('swiper-initialized');
    
    // Count slides - don't initialize if 4 or fewer blocks
    const slideCount = swiperContainer.querySelectorAll('.swiper-slide').length;
    const targetedProducts = document.querySelector('.targeted-products');
    if(slideCount <= 4) {
      targetedProducts?.classList.add('hide-pagination');
    }
    
    // Get settings from the swiper container's data attributes
    const showArrows = swiperContainer.dataset.showArrows !== 'false';
    const autoplay = swiperContainer.dataset.autoplay === 'true';
    const autoplaySpeed = swiperContainer.dataset.autoplaySpeed || 5000;
    const infinite = swiperContainer.dataset.infinite !== 'false';
    
    // Get column count from dataset or fallback to CSS variables
    const columnCount = parseInt(swiperContainer.dataset.columnCount) || 
                       parseInt(swiperContainer.closest('[style*="--targeted-products-column-count"]')?.style.getPropertyValue('--targeted-products-column-count')) || 
                       4;
    const mobileColumnCount = parseInt(swiperContainer.dataset.mobileColumnCount) || 
                             parseInt(swiperContainer.closest('[style*="--targeted-products-mobile-column-count"]')?.style.getPropertyValue('--targeted-products-mobile-column-count')) || 
                             2;
    
    // Find the navigation buttons in the parent wrapper
    const wrapper = swiperContainer.closest('.swiper-container-wrapper');
    const nextButton = wrapper ? wrapper.querySelector('.targeted-products-nav-next') : null;
    const prevButton = wrapper ? wrapper.querySelector('.targeted-products-nav-prev') : null;
    
    // Force buttons to be visible
    if (nextButton) {
      nextButton.style.display = 'flex';
      nextButton.style.visibility = 'visible';
      nextButton.style.opacity = '1';
      nextButton.classList.remove('button-lock');
    }
    
    if (prevButton) {
      prevButton.style.display = 'flex';
      prevButton.style.visibility = 'visible';
      prevButton.style.opacity = '1';
      prevButton.classList.remove('button-lock');
    }
    
    // Targeted Products swiper configuration
    // OPTIMIZED: Use correct initial column count immediately
    const viewportWidth = window.innerWidth;
    let initialColumnCount = 1;
    if (viewportWidth >= 1024) {
      initialColumnCount = columnCount;
    } else if (viewportWidth >= 768) {
      initialColumnCount = 3;
    } else {
      initialColumnCount = 1.1; // Mobile peek
    }
    
    const maxSlidesPerView = Math.max(columnCount, 4);
    const canLoop = infinite && slideCount >= maxSlidesPerView * 2;

    const swiperConfig = {
      loop: canLoop,
      speed: 400,
      slidesPerView: initialColumnCount, // Use calculated initial column count immediately
      spaceBetween: 16,
      grabCursor: true,
      touchAngle: 45,
      threshold: 5,
      resistanceRatio: 0.85,
      longSwipesRatio: 0.5,
      shortSwipes: true,
      followFinger: true,
      navigation: nextButton && prevButton ? {
        nextEl: nextButton,
        prevEl: prevButton,
      } : false,
      breakpoints: {
        320: {
          slidesPerView: 1.1,
          spaceBetween: 16,
          slidesPerGroup: 1
        },
        480: {
          slidesPerView: 1.1,
          spaceBetween: 16,
          slidesPerGroup: 1
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 16,
          slidesPerGroup: 1
        },
        1024: {
          slidesPerView: columnCount,
          spaceBetween: 16,
          slidesPerGroup: 1,
          navigation: false
        }
      },
    };

    // Guard: Swiper may not load if CDN returns 502/503/504
    if (typeof window.Swiper !== 'function') {
      console.warn('[SuniceTargetedProductsSwiper] Swiper not loaded - check CDN/network (502/503/504)');
      return null;
    }

    // Initialize targeted products swiper
    const swiper = new window.Swiper(swiperContainer, swiperConfig);
    this.swiperInstances.set(containerId, swiper);

    // Mark as initialized immediately so lazy-loaded sections can reveal on next frame
    swiperContainer.classList.add('swiper-initialized');
    requestAnimationFrame(() => {
      swiperContainer.style.minHeight = swiperContainer.offsetHeight + 'px'; // Prevent CLS
      void swiperContainer.offsetWidth; // Force reflow for transition
    });

    console.log('Targeted products swiper initialized successfully:', swiper);
    return swiper;
  }

  destroyTargetedProductsSwipers() {
    this.swiperInstances.forEach((swiper, id) => {
      if (swiper && swiper.destroy) {
        swiper.destroy(true, true);
      }
    });
    this.swiperInstances.clear();
  }
}

// Export for use in main swiper file
// @ts-ignore
window.SuniceTargetedProductsSwiper = SuniceTargetedProductsSwiper;
